export const MODULES = [
  { id: "tables", label: "Tables", path: "/database-designer" },
  { id: "relationships", label: "Relationships", path: "/database-designer/relationships" },
  { id: "queries", label: "Queries", path: "/database-designer/queries" },
  { id: "forms", label: "Forms", path: "/database-designer/forms" },
  { id: "reports", label: "Reports", path: "/database-designer/reports" },
  { id: "application", label: "Application", path: "/database-designer/application" },
];

export const DATA_TYPES = [
  { id: "uuid", label: "UUID" },
  { id: "text", label: "Text" },
  { id: "varchar", label: "Text" },
  { id: "integer", label: "Integer" },
  { id: "bigint", label: "Integer" },
  { id: "numeric", label: "Numeric" },
  { id: "boolean", label: "Boolean" },
  { id: "date", label: "Date" },
  { id: "timestamp", label: "Timestamp" },
  { id: "timestamptz", label: "Timestamp" },
  { id: "time", label: "Time" },
  { id: "jsonb", label: "JSONB" },
];

export const GRID_TYPE_OPTIONS = [
  { id: "uuid", label: "UUID" },
  { id: "text", label: "Text" },
  { id: "varchar", label: "VARCHAR" },
  { id: "integer", label: "Integer" },
  { id: "bigint", label: "BIGINT" },
  { id: "numeric", label: "Numeric" },
  { id: "boolean", label: "Boolean" },
  { id: "date", label: "Date" },
  { id: "timestamp", label: "Timestamp" },
  { id: "timestamptz", label: "TIMESTAMPTZ" },
  { id: "time", label: "Time" },
  { id: "jsonb", label: "JSONB" },
];

/** Same identifier rule the backend uses for new field names. */
export const FIELD_NAME_RE = /^[a-z][a-z0-9_]{0,62}$/;

/** PostgreSQL reserved words the designer must not save as new identifiers. */
export const RESERVED_IDENTIFIERS = new Set([
  "all", "analyse", "analyze", "and", "any", "array", "as", "asc", "asymmetric",
  "authorization", "binary", "both", "case", "cast", "check", "collate", "column",
  "concurrently", "constraint", "create", "cross", "current_catalog", "current_date",
  "current_role", "current_schema", "current_time", "current_timestamp", "current_user",
  "default", "deferrable", "desc", "distinct", "do", "else", "end", "except", "false",
  "fetch", "for", "foreign", "freeze", "from", "full", "grant", "group", "having",
  "ilike", "in", "initially", "inner", "intersect", "into", "is", "isnull", "join",
  "lateral", "leading", "left", "like", "limit", "localtime", "localtimestamp",
  "natural", "not", "notnull", "null", "offset", "on", "only", "or", "order", "outer",
  "overlaps", "placing", "primary", "references", "returning", "right", "select",
  "session_user", "similar", "some", "symmetric", "table", "tablesample", "then",
  "to", "trailing", "true", "union", "unique", "user", "using", "variadic", "verbose",
  "when", "where", "window", "with",
]);

export const LOOKUP_KINDS = [
  { id: "none", label: "None" },
  { id: "static", label: "List of values" },
  { id: "reference", label: "Related table" },
];

/**
 * The catalog is the PostgreSQL types Table Designer can save. A live column
 * of any other type must still appear in the list instead of rendering blank.
 */
export function typeOptions(dataType) {
  const current = String(dataType || "").trim().toLowerCase();
  if (!current || GRID_TYPE_OPTIONS.some((option) => option.id === current)) {
    return GRID_TYPE_OPTIONS;
  }
  const label = typeLabel(current);
  const collides = GRID_TYPE_OPTIONS.some((option) => option.label === label);
  return [...GRID_TYPE_OPTIONS, { id: current, label: collides ? `${label} (${current})` : label }];
}

export function moduleFromPath(pathname) {
  const path = pathname || "";
  if (path.startsWith("/database-designer/relationships")) return "relationships";
  if (path.startsWith("/database-designer/queries")) return "queries";
  if (path.startsWith("/database-designer/forms") || path.startsWith("/form-designer")) return "forms";
  if (path.startsWith("/database-designer/reports")) return "reports";
  if (path.startsWith("/database-designer/application")) return "application";
  return "tables";
}

export function typeLabel(id) {
  return DATA_TYPES.find((item) => item.id === id)?.label || id || "Text";
}

export function emptyLookup() {
  return { kind: "none", options: [], refTable: "", refColumn: "", refDisplay: "" };
}

let fieldKeySeq = 0;

/** Stable React identity so renaming a field does not remount its row. */
export function nextFieldKey() {
  fieldKeySeq += 1;
  return `fld-${fieldKeySeq}`;
}

export function withFieldKeys(columns) {
  return (columns || []).map((column) => (column.fieldKey ? column : { ...column, fieldKey: nextFieldKey() }));
}

export function emptyField(overrides = {}) {
  return {
    name: "",
    dataType: "text",
    length: null,
    precision: null,
    scale: null,
    required: false,
    primaryKey: false,
    unique: false,
    indexed: false,
    defaultValue: "",
    description: "",
    lookup: emptyLookup(),
    fieldKey: nextFieldKey(),
    ...overrides,
  };
}

export function typeHasLength(dataType) {
  return String(dataType || "").trim().toLowerCase() === "varchar";
}

export function typeHasNumeric(dataType) {
  return String(dataType || "").trim().toLowerCase() === "numeric";
}

export function uniqueFieldName(columns, base = "new_field") {
  const taken = new Set((columns || []).map((column) => String(column.name || "").trim()));
  const root = String(base || "new_field").replace(/_copy\d*$/, "") || "new_field";
  if (!taken.has(root)) return root;
  let name = `${root}_copy`;
  let n = 2;
  while (taken.has(name)) {
    name = `${root}_copy${n}`;
    n += 1;
  }
  return name;
}

export function fieldForeignKey(table, columnName) {
  return (table?.foreignKeys || []).find((fk) => (fk.columns || [])[0] === columnName) || null;
}

export function foreignKeyLabel(fk) {
  if (!fk) return "";
  const refColumn = (fk.refColumns || [])[0] || "";
  return refColumn ? `${fk.refTable}.${refColumn}` : String(fk.refTable || "");
}

export function designSnapshot(table) {
  if (!table) return "";
  return JSON.stringify({
    name: table.name,
    description: table.description || "",
    columns: (table.columns || []).map((column) => ({
      name: column.name,
      dataType: column.dataType,
      length: column.length || null,
      precision: column.precision || null,
      scale: column.scale || null,
      required: Boolean(column.required || column.primaryKey),
      primaryKey: Boolean(column.primaryKey),
      unique: Boolean(column.unique),
      indexed: Boolean(column.indexed),
      defaultValue: column.defaultValue || "",
      description: column.description || "",
      lookup: column.lookup || emptyLookup(),
    })),
    foreignKeys: table.foreignKeys || [],
  });
}

/**
 * Client-side save gate. The API still validates; this keeps the obvious
 * mistakes in the designer instead of a toast from the host.
 */
export function validateTableSpec(table) {
  const errors = [];
  const columns = table?.columns || [];
  if (!columns.length) {
    errors.push("A table must have at least one field.");
    return { ok: false, errors };
  }

  const names = new Set();
  let primaryKeys = 0;
  columns.forEach((column, index) => {
    const label = `Field ${index + 1}`;
    const name = String(column.name || "").trim();
    if (!name) {
      errors.push(`${label} needs a field name.`);
    } else if (!FIELD_NAME_RE.test(name)) {
      errors.push(`"${name}" must start with a lowercase letter and use only letters, digits, or underscore.`);
    } else if (RESERVED_IDENTIFIERS.has(name)) {
      errors.push(`"${name}" is a reserved PostgreSQL word.`);
    } else if (names.has(name)) {
      errors.push(`Field name "${name}" is used more than once.`);
    }
    if (name) names.add(name);

    if (column.primaryKey) primaryKeys += 1;

    if (typeHasLength(column.dataType) && column.length != null && column.length !== "") {
      const length = Number(column.length);
      if (!Number.isInteger(length) || length < 1 || length > 10485760) {
        errors.push(`${name || label} VARCHAR length must be between 1 and 10485760.`);
      }
    }

    if (typeHasNumeric(column.dataType) && (column.precision != null && column.precision !== "")) {
      const precision = Number(column.precision);
      const scale = Number(column.scale || 0);
      if (!Number.isInteger(precision) || precision < 1 || precision > 1000 || scale < 0 || scale > precision) {
        errors.push(`${name || label} NUMERIC precision/scale is invalid.`);
      }
    }

    const lookup = column.lookup || emptyLookup();
    if (lookup.kind === "static" && !(lookup.options || []).filter(Boolean).length) {
      errors.push(`${name || label} lookup needs at least one list value.`);
    }
    if (lookup.kind === "reference" && !String(lookup.refTable || "").trim()) {
      errors.push(`${name || label} lookup needs a related table.`);
    }
  });

  if (primaryKeys === 0) {
    errors.push("Choose a primary key before saving.");
  }

  return { ok: errors.length === 0, errors };
}

/** Grid flag cell. Design View shows the field definition, not an editor. */
export function flagMark(value) {
  return value ? "✓" : "";
}

/** Properties panel reads as a sentence, so booleans spell out. */
export function yesNo(value) {
  return value ? "Yes" : "No";
}

export function defaultPrimaryKey() {
  return emptyField({
    name: "id",
    dataType: "uuid",
    required: true,
    primaryKey: true,
    defaultValue: "gen_random_uuid()",
    description: "Primary key",
  });
}

export function newTableSpec(name = "new_table", description = "") {
  return {
    schema: "public",
    name,
    description,
    isNew: true,
    protected: false,
    origin: "designer",
    columns: [defaultPrimaryKey()],
    indexes: [],
    foreignKeys: [],
    primaryKey: ["id"],
  };
}

export function humanizeField(name) {
  return String(name || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function toSavePayload(table) {
  return {
    schema: table.schema || "public",
    name: table.name,
    description: table.description || "",
    columns: (table.columns || []).map((column) => ({
      name: column.name,
      dataType: column.dataType,
      length: column.length || null,
      precision: column.precision || null,
      scale: column.scale || null,
      required: Boolean(column.required || column.primaryKey),
      primaryKey: Boolean(column.primaryKey),
      unique: Boolean(column.unique),
      indexed: Boolean(column.indexed),
      defaultValue: column.defaultValue || null,
      description: column.description || "",
      lookup: column.lookup || emptyLookup(),
    })),
    indexes: table.indexes || [],
    foreignKeys: table.foreignKeys || [],
  };
}
